import { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UserPlus, Loader2, User, Mail, Phone, CreditCard, Users, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { adminUsersAPI, teamsV2API } from '@/lib/api';
import { invalidatePrefix } from '@/lib/dataCache';
import { useAuth } from '@/lib/auth';

export function CreateApporteurDialog({ open, onOpenChange, onCreated }) {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin' || user?.role === 'admin_full' || user?.is_admin === true;
    const [loading, setLoading] = useState(false);
    const [teams, setTeams] = useState([]);
    const [allTeams, setAllTeams] = useState([]);
    const [vpUsers, setVpUsers] = useState([]);
    const [selectedVpId, setSelectedVpId] = useState('');
    const [selectedTeamId, setSelectedTeamId] = useState('');
    
    const [formData, setFormData] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        iban: '',
    });

    // Charger les équipes (VP : les siennes ; Admin : toutes + liste des VP)
    useEffect(() => {
        if (open && user?.id) {
            loadTeams();
        }
    }, [open, user?.id]);

    const loadTeams = async () => {
        try {
            const res = await teamsV2API.getAll();
            const all = res.data || [];
            setAllTeams(all);
            if (isAdmin) {
                // Admin : choisit le VP cible puis son équipe
                try {
                    const usersRes = await adminUsersAPI.getAll();
                    setVpUsers((usersRes.data || []).filter(u => u.role === 'vp' && u.is_active));
                } catch (e) {
                    console.error('Erreur chargement VP:', e);
                }
            } else {
                // VP : ses propres équipes
                const vpTeams = all.filter(t => t.payer_vp_id === user.id || t.supervisor_vp_id === user.id);
                setTeams(vpTeams);
                if (vpTeams.length === 1) setSelectedTeamId(vpTeams[0].id);
            }
        } catch (error) {
            console.error('Erreur chargement équipes:', error);
        }
    };

    // Admin : quand un VP est choisi, filtrer ses équipes
    useEffect(() => {
        if (isAdmin && selectedVpId) {
            const vpTeams = allTeams.filter(t => t.payer_vp_id === selectedVpId || t.supervisor_vp_id === selectedVpId);
            setTeams(vpTeams);
            setSelectedTeamId(vpTeams.length === 1 ? vpTeams[0].id : '');
        }
    }, [selectedVpId, isAdmin, allTeams]);

    const handleChange = (e) => {
        setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validation
        if (!formData.first_name.trim() || !formData.last_name.trim()) {
            toast.error('Nom et prénom sont obligatoires');
            return;
        }
        if (!formData.email.trim() || !formData.email.includes('@')) {
            toast.error('Email invalide');
            return;
        }
        // Admin : un VP cible est obligatoire
        if (isAdmin && !selectedVpId) {
            toast.error('Veuillez sélectionner un VP');
            return;
        }
        // Si plusieurs équipes, une doit être sélectionnée
        if (teams.length > 1 && !selectedTeamId) {
            toast.error('Veuillez sélectionner une équipe');
            return;
        }
        
        const vpId = isAdmin ? selectedVpId : user.id;
        const teamId = selectedTeamId || (teams.length === 1 ? teams[0].id : null);

        setLoading(true);
        try {
            // Générer un mot de passe temporaire
            const tempPassword = `Valnexo${Math.random().toString(36).substring(2, 8)}!`;
            
            const payload = {
                first_name: formData.first_name.trim(),
                last_name: formData.last_name.trim(),
                email: formData.email.trim().toLowerCase(),
                phone: formData.phone.trim() || null,
                password: tempPassword,
                role: 'apporteur',
                vp_id: vpId,
                team_id: teamId,
            };
            
            const res = await adminUsersAPI.create(payload);
            const newApporteurId = res.data?.user_id;

            // Rattachement automatique réel à l'équipe (member_ids)
            if (teamId && newApporteurId) {
                try {
                    await teamsV2API.addMember(teamId, { user_id: newApporteurId, role_in_team: 'member' });
                } catch (memberErr) {
                    console.error('Erreur rattachement équipe:', memberErr);
                }
            }

            invalidatePrefix('dashboard');
            invalidatePrefix('pilotage');
            
            toast.success(
                <div>
                    <p className="font-semibold">Apporteur créé avec succès !</p>
                    <p className="text-sm text-slate-600">Un email de bienvenue a été envoyé à {formData.email}</p>
                </div>
            );
            
            // Reset form
            setFormData({
                first_name: '',
                last_name: '',
                email: '',
                phone: '',
                iban: '',
            });
            setSelectedTeamId('');
            setSelectedVpId('');
            
            onOpenChange(false);
            onCreated?.();
        } catch (error) {
            console.error('Erreur création apporteur:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <UserPlus className="w-5 h-5 text-green-600" />
                        Créer un Apporteur
                    </DialogTitle>
                    <DialogDescription>
                        Ajoutez un nouvel apporteur à votre équipe. Un email avec ses identifiants lui sera envoyé automatiquement.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                    {/* Sélection du VP (Admin uniquement) */}
                    {isAdmin && (
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Crown className="w-4 h-4 text-amber-500" />
                                VP référent *
                            </Label>
                            <Select value={selectedVpId} onValueChange={setSelectedVpId}>
                                <SelectTrigger data-testid="select-vp">
                                    <SelectValue placeholder="Choisir un VP..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {vpUsers.map((vp) => (
                                        <SelectItem key={vp.id} value={vp.id}>
                                            {vp.first_name} {vp.last_name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {selectedVpId && teams.length === 0 && (
                                <p className="text-xs text-amber-600">Ce VP n'a pas encore d'équipe. L'apporteur sera créé et rattaché au VP.</p>
                            )}
                        </div>
                    )}

                    {/* Sélection équipe si plusieurs */}
                    {teams.length > 1 && (
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2">
                                <Users className="w-4 h-4" />
                                Équipe *
                            </Label>
                            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
                                <SelectTrigger data-testid="select-team">
                                    <SelectValue placeholder="Choisir une équipe..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {teams.map((team) => (
                                        <SelectItem key={team.id} value={team.id}>
                                            {team.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    
                    {teams.length === 1 && (
                        <div className="p-3 bg-slate-50 rounded-lg text-sm">
                            <span className="text-slate-500">Équipe :</span>{' '}
                            <span className="font-medium">{teams[0].name}</span>
                        </div>
                    )}

                    {/* Nom / Prénom */}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-2">
                            <Label htmlFor="first_name" className="flex items-center gap-2">
                                <User className="w-4 h-4" />
                                Prénom *
                            </Label>
                            <Input
                                id="first_name"
                                name="first_name"
                                value={formData.first_name}
                                onChange={handleChange}
                                placeholder="Jean"
                                required
                                data-testid="input-first-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="last_name">Nom *</Label>
                            <Input
                                id="last_name"
                                name="last_name"
                                value={formData.last_name}
                                onChange={handleChange}
                                placeholder="Dupont"
                                required
                                data-testid="input-last-name"
                            />
                        </div>
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                        <Label htmlFor="email" className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Email *
                        </Label>
                        <Input
                            id="email"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="jean.dupont@email.com"
                            required
                            data-testid="input-email"
                        />
                    </div>

                    {/* Téléphone */}
                    <div className="space-y-2">
                        <Label htmlFor="phone" className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            Téléphone
                        </Label>
                        <Input
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            placeholder="06 12 34 56 78"
                            data-testid="input-phone"
                        />
                    </div>

                    {/* IBAN */}
                    <div className="space-y-2">
                        <Label htmlFor="iban" className="flex items-center gap-2">
                            <CreditCard className="w-4 h-4" />
                            IBAN
                        </Label>
                        <Input
                            id="iban"
                            name="iban"
                            value={formData.iban}
                            onChange={handleChange}
                            placeholder="FR76 XXXX XXXX XXXX XXXX XXXX XXX"
                            data-testid="input-iban"
                        />
                        <p className="text-xs text-slate-500">Sera utilisé pour les paiements de commissions</p>
                    </div>

                    {/* Commission : gérée sur la page Équipe (source unique) */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-sm text-blue-800 font-medium">Commission de l'apporteur</p>
                        <p className="text-xs text-blue-600 mt-1" data-testid="apporteur-commission-info">
                            Le pourcentage de cet apporteur se règle directement dans son équipe
                            (page <span className="font-medium">Équipe → « Modifier commission »</span>),
                            pour Easy Power et Chantier. C'est l'unique source utilisée dans les calculs.
                        </p>
                    </div>

                    <DialogFooter className="mt-6">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                            Annuler
                        </Button>
                        <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700">
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Création...
                                </>
                            ) : (
                                <>
                                    <UserPlus className="w-4 h-4 mr-2" />
                                    Créer l'apporteur
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default CreateApporteurDialog;
